import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import type { Bike, BikeWithLocation, BikeStats } from "../types/bike";
import { apiService } from "../services/api";
import { isTimestampWithinWindow } from "../utils/helpers";
import { useAuthStore } from "./useAuthStore";
import { isFleetScopedRole } from "../utils/authRoles";
type BikeQueryParams = Parameters<typeof apiService.getBikes>[0];

interface RealtimeBikeUpdate {
  bike_id: string;
  latitude: number;
  longitude: number;
  status: string;
  timestamp: string;
  speed?: number;
  battery_level?: number;
}

interface BikeState {
  // Data
  bikes: BikeWithLocation[];
  selectedBike: string | null;
  bikeStats: BikeStats | null;

  // Pagination metadata
  totalCount: number;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;

  // Loading states
  loading: boolean;
  loadingStats: boolean;
  isRefreshing: boolean;
  hasLoaded: boolean;
  lastFetchedAt: string | null;

  // Actions
  /**
   * @deprecated Use getAllBikes instead.
   */
  fetchBikes: (params?: Record<string, unknown>) => Promise<void>;
  loadIfNeeded: () => Promise<void>;
  /**
   * @deprecated We can get the bike stats from the bike list.
   */
  fetchBikeStats: () => Promise<void>;
  selectBike: (bikeId: string | null) => void;
  addBike: (bike: Omit<Bike, "created_at" | "updated_at">) => Promise<void>;
  updateBike: (
    bikeId: string,
    updates: Partial<
      Pick<
        Bike,
        | "status"
        | "category_id"
        | "bike_sn"
        | "max_speed"
        | "comment"
        | "primary_city_id"
        | "fleet_id"
        | "ui_hidden"
      >
    >,
  ) => Promise<void>;
  removeBike: (bikeId: string) => Promise<void>;
  refreshBikes: (opts?: { include_hidden?: boolean }) => Promise<void>;
  upsertBikeFromRealtime: (update: RealtimeBikeUpdate) => void;
}

export const useBikeStore = create<BikeState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        bikes: [],
        selectedBike: null,
        bikeStats: null,
        totalCount: 0,
        currentPage: 1,
        totalPages: 1,
        itemsPerPage: 20,
        loading: false,
        loadingStats: false,
        isRefreshing: false,
        hasLoaded: false,
        lastFetchedAt: null,

        // Actions
        /**
         * @deprecated Use getAllBikes instead.
         */
        fetchBikes: async (
          params = { include_location: true, page: 1, limit: 20 },
        ) => {
          set({ loading: true });
          try {
            const response = (await apiService.getBikes(
              params as BikeQueryParams,
            )) as {
              bikes: BikeWithLocation[];
              pagination?: {
                total: number;
                page: number;
                limit: number;
                totalPages: number;
              };
            };

            const pagination = response.pagination || {
              total: response.bikes.length,
              page: 1,
              limit: response.bikes.length,
              totalPages: 1,
            };

            set({
              bikes: response.bikes,
              totalCount: pagination.total,
              currentPage: pagination.page,
              totalPages: pagination.totalPages,
              itemsPerPage: pagination.limit,
              hasLoaded: true,
              lastFetchedAt: new Date().toISOString(),
              loading: false,
            });
          } catch (error) {
            console.error("Error fetching bikes:", error);
            set({ loading: false });
          }
        },

        loadIfNeeded: async () => {
          const {
            hasLoaded,
            bikes,
            isRefreshing,
            loading,
            lastFetchedAt,
            refreshBikes,
          } = get();
          if (isRefreshing || loading) {
            return;
          }
          if (
            hasLoaded &&
            bikes.length > 0 &&
            isTimestampWithinWindow(lastFetchedAt, 15 * 60 * 1000, Date.now())
          ) {
            return;
          }
          await refreshBikes();
        },

        /**
         * @deprecated We can get the bike stats from the bike list.
         */
        fetchBikeStats: async () => {
          set({ loadingStats: true });
          try {
            const stats = (await apiService.getBikeStatistics()) as BikeStats;
            set({ bikeStats: stats, loadingStats: false });
          } catch (error) {
            console.error("Error fetching bike stats:", error);
            set({ loadingStats: false });
          }
        },

        selectBike: (bikeId) => set({ selectedBike: bikeId }),

        addBike: async (bike) => {
          try {
            const newBike = (await apiService.createBike(bike)) as Bike;
            const { bikes } = get();
            set({
              bikes: [newBike as BikeWithLocation, ...bikes],
              totalCount: bikes.length + 1,
            });
          } catch (error) {
            console.error("Error adding bike:", error);
            throw error;
          }
        },

        updateBike: async (bikeId, updates) => {
          try {
            const updatedBike = (await apiService.updateBike(
              bikeId,
              updates,
            )) as Bike;
            const { bikes } = get();
            const updatedBikes = bikes.map((bike) =>
              bike.bike_id === bikeId ? { ...bike, ...updatedBike } : bike,
            );
            set({ bikes: updatedBikes });
          } catch (error) {
            console.error("Error updating bike:", error);
            throw error;
          }
        },

        removeBike: async (bikeId) => {
          try {
            await apiService.deleteBike(bikeId);
            const { bikes } = get();
            const filteredBikes = bikes.filter(
              (bike) => bike.bike_id !== bikeId,
            );
            set({ bikes: filteredBikes, totalCount: filteredBikes.length });
          } catch (error) {
            console.error("Error removing bike:", error);
            throw error;
          }
        },

        refreshBikes: async (opts?: { include_hidden?: boolean }) => {
          set({ isRefreshing: true, loading: true });
          try {
            const response = (await apiService.getAllBikes({
              include_location: true,
              sort_by: "bike_id",
              sort_order: "asc",
              ...(opts?.include_hidden ? { include_hidden: true } : {}),
            })) as {
              bikes: BikeWithLocation[];
            };
            set({
              bikes: response.bikes,
              totalCount: response.bikes.length,
              currentPage: 1,
              totalPages: 1,
              itemsPerPage: response.bikes.length || 1,
              hasLoaded: true,
              lastFetchedAt: new Date().toISOString(),
              isRefreshing: false,
              loading: false,
            });
          } catch (error) {
            console.error("Error refreshing bikes:", error);
            set({ isRefreshing: false, loading: false });
          }
        },

        upsertBikeFromRealtime: (update) => {
          const now = new Date().toISOString();
          set((state) => {
            const existingIndex = state.bikes.findIndex(
              (bike) => bike.bike_id === update.bike_id,
            );

            if (existingIndex >= 0 && state.bikes[existingIndex].ui_hidden) {
              return {};
            }

            const realtimeLocation = {
              latitude: update.latitude,
              longitude: update.longitude,
              timestamp: update.timestamp,
              speed: update.speed,
              battery_level: update.battery_level,
            };

            if (existingIndex >= 0) {
              const nextBikes = [...state.bikes];
              nextBikes[existingIndex] = {
                ...nextBikes[existingIndex],
                status: update.status as Bike["status"],
                last_location: realtimeLocation,
                updated_at: now,
              };
              return { bikes: nextBikes };
            }

            const role = useAuthStore.getState().user?.role;
            if (isFleetScopedRole(role)) {
              return {};
            }

            const placeholderBike: BikeWithLocation = {
              bike_id: update.bike_id,
              status: (update.status as Bike["status"]) || "active",
              category_id: null,
              bike_sn: null,
              max_speed: null,
              comment: null,
              primary_city_id: null,
              ui_hidden: false,
              created_at: now,
              updated_at: now,
              last_location: realtimeLocation,
            };

            return {
              bikes: [placeholderBike, ...state.bikes],
              totalCount: state.totalCount + 1,
            };
          });
        },
      }),
      {
        name: "bike-store",
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          bikes: state.bikes,
          hasLoaded: state.hasLoaded,
          lastFetchedAt: state.lastFetchedAt,
          totalCount: state.totalCount,
        }),
      },
    ),
    { name: "bike-store-devtools" },
  ),
);
